import React, {
  useEffect,
  useState,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";
import { API } from "../api";

import { toasts } from "../components/AppToast";

import Layout from "../components/Layout";

import {
  FiTrash2,
  FiEdit,
  FiPlus,
  FiClipboard,
  FiClock,
} from "react-icons/fi";

const EMPTY_FORM = {
  season_id: "",
  season_dur_fromdate: "",
  season_dur_todate: "",
};

export default function SeasonDuration() {
  const navigate = useNavigate();

  // =========================================================
  // USER
  // =========================================================

  const [user, setUser] = useState(null);

  // =========================================================
  // SEASONS DROPDOWN
  // =========================================================

  const [seasons, setSeasons] = useState([]);

  // =========================================================
  // TABLE
  // =========================================================

  const [data, setData] = useState([]);

  const [page, setPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [total, setTotal] = useState(0);

  const [filterSeason, setFilterSeason] = useState(0);

  const [loading, setLoading] = useState(false);

  // =========================================================
  // FORM
  // =========================================================

  const [form, setForm] = useState(EMPTY_FORM);

  const [editId, setEditId] = useState(null);

  const [saving, setSaving] = useState(false);

  // =========================================================
  // ADD / EDIT MODAL
  // =========================================================

  const [showModal, setShowModal] = useState(false);

  // =========================================================
  // DELETE MODAL
  // =========================================================

  const [delModal, setDelModal] = useState(null);

  const [delSaving, setDelSaving] = useState(false);

  // =========================================================
  // AUTH
  // =========================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setUser(
        JSON.parse(
          localStorage.getItem("user") || "null"
        )
      );
    } catch (error) {
      console.error(
        "User parse error:",
        error
      );

      setUser(null);
    }
  }, [navigate]);

  // =========================================================
  // LOAD SEASONS
  // =========================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    API.get("/seasons", {
      params: {
        page: 1,
        limit: 100,
        search: "",
      },
    })
      .then((response) => {
        setSeasons(
          response.data?.data || []
        );
      })
      .catch((err) => {
        console.error(
          "Failed to load seasons:",
          err
        );

        toasts.error(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to load seasons"
        );
      });
  }, []);

  // =========================================================
  // FETCH DURATIONS
  // =========================================================

  const fetchData = useCallback(
    async (pg, sid) => {
      try {
        setLoading(true);

        const response = await API.get(
          "/durations",
          {
            params: {
              page: pg,
              limit: 10,
              season_id: sid,
            },
          }
        );

        console.log(
          "Pagination API response:",
          response.data
        );

        setData(
          response.data?.data || []
        );

        setTotal(
          Number(
            response.data?.total || 0
          )
        );

        setTotalPages(
          Number(
            response.data?.totalPages || 1
          )
        );
      } catch (err) {
        console.error(
          "Failed to load durations:",
          err
        );

        toasts.error(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to load durations"
        );

        setData([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // =========================================================
  // LOAD TABLE
  // =========================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    fetchData(
      page,
      filterSeason
    );
  }, [
    page,
    filterSeason,
    fetchData,
  ]);

  // =========================================================
  // FILTER
  // =========================================================

  const handleFilterChange = (value) => {
    const sid =
      parseInt(value, 10) || 0;

    setFilterSeason(sid);

    setPage(1);
  };

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // OPEN ADD MODAL
  // =========================================================

  const handleAdd = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);

    setShowModal(true);
  };

  // =========================================================
  // OPEN EDIT MODAL
  // =========================================================

  const handleEdit = (row) => {
    const fromDate = String(
      row.season_dur_fromdate || ""
    ).slice(0, 10);

    const toDate = String(
      row.season_dur_todate || ""
    ).slice(0, 10);

    setEditId(
      row.season_durid
    );

    setForm({
      season_id:
        row.season_id || "",
      season_dur_fromdate:
        fromDate,
      season_dur_todate:
        toDate,
    });

    setShowModal(true);
  };

  // =========================================================
  // CANCEL / RESET FORM
  // =========================================================

  const handleCancel = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);

    setShowModal(false);
  };

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {
    // -------------------------------------------------------
    // VALIDATION
    // -------------------------------------------------------

    if (!form.season_id) {
      toasts.warning(
        "Select a season"
      );
      return;
    }

    if (!form.season_dur_fromdate) {
      toasts.warning(
        "Select From Date"
      );
      return;
    }

    if (!form.season_dur_todate) {
      toasts.warning(
        "Select To Date"
      );
      return;
    }

    if (
      form.season_dur_fromdate >=
      form.season_dur_todate
    ) {
      toasts.warning(
        "From Date must be before To Date"
      );
      return;
    }

    // -------------------------------------------------------
    // USER CHECK
    // -------------------------------------------------------

    if (!user?.user_id) {
      toasts.error(
        "User information not available"
      );
      return;
    }

    setSaving(true);

    // -------------------------------------------------------
    // PAYLOAD
    // -------------------------------------------------------

    const payload = {
      season_id: parseInt(
        form.season_id,
        10
      ),

      season_dur_fromdate:
        form.season_dur_fromdate,

      season_dur_todate:
        form.season_dur_todate,

      user_id: user.user_id,
    };

    try {
      // -----------------------------------------------------
      // UPDATE
      // -----------------------------------------------------

      if (editId) {
        await API.put(
          `/durations/${editId}`,
          payload
        );

        toasts.success(
          "Duration updated successfully"
        );
      }

      // -----------------------------------------------------
      // ADD
      // -----------------------------------------------------

      else {
        await API.post(
          "/durations",
          payload
        );

        toasts.success(
          "Duration added successfully"
        );
      }

      // -----------------------------------------------------
      // RESET
      // -----------------------------------------------------

      setForm({
        ...EMPTY_FORM,
      });

      setEditId(null);

      setShowModal(false);

      // -----------------------------------------------------
      // RELOAD
      // -----------------------------------------------------

      await fetchData(
        page,
        filterSeason
      );
    } catch (err) {
      console.error(
        "Submit error:",
        err
      );

      toasts.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // OPEN DELETE MODAL
  // =========================================================

  const openDeleteModal = (row) => {
    setDelModal(row);
  };

  // =========================================================
  // CLOSE DELETE MODAL
  // =========================================================

  const closeDeleteModal = () => {
    if (delSaving) {
      return;
    }

    setDelModal(null);
  };

  // =========================================================
  // DELETE
  // =========================================================

  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(
        `/durations/${delModal.season_durid}`
      );

      toasts.success(
        "Season duration deleted successfully"
      );

      // -----------------------------------------------------
      // CLOSE MODAL
      // -----------------------------------------------------

      setDelModal(null);

      // -----------------------------------------------------
      // CALCULATE NEW PAGINATION
      // -----------------------------------------------------

      const remainingRecords =
        Math.max(
          0,
          total - 1
        );

      const newTotalPages =
        Math.max(
          1,
          Math.ceil(
            remainingRecords / 10
          )
        );

      // -----------------------------------------------------
      // CURRENT PAGE NO LONGER EXISTS
      // -----------------------------------------------------

      if (
        page > newTotalPages
      ) {
        setPage(
          newTotalPages
        );

        return;
      }

      // -----------------------------------------------------
      // RELOAD CURRENT PAGE
      // -----------------------------------------------------

      await fetchData(
        page,
        filterSeason
      );
    } catch (err) {
      console.error(
        "Delete error:",
        err
      );

      toasts.error(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const fmtDate = (date) => {
    if (!date) {
      return "—";
    }

    const parsedDate =
      new Date(date);

    if (
      Number.isNaN(
        parsedDate.getTime()
      )
    ) {
      return "—";
    }

    return parsedDate.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Layout activePage="SeasonDuration">

      <main className="dashboard-main">

        {/* ===================================================
            TABLE CARD
        =================================================== */}

        <div className="table-card">

          {/* =================================================
              TABLE HEADER
          ================================================= */}

          <div className="table-card-head">

            <h3 className="table-card-title d-flex align-items-center">

              <FiClipboard
                size={26}
                className="me-2"
              />

              <span>
                Manage Season Duration
              </span>

            </h3>

            {/* FILTER */}

            <select
              value={filterSeason}
              onChange={(e) =>
                handleFilterChange(
                  e.target.value
                )
              }
              className="season-input"
              style={{
                maxWidth: "220px",
              }}
            >

              <option value={0}>
                — All Seasons —
              </option>

              {seasons.map((season) => (
                <option
                  key={
                    season.season_id
                  }
                  value={
                    season.season_id
                  }
                >
                  {
                    season.season_name
                  }
                </option>
              ))}

            </select>

            {/* ADD BUTTON */}

            <button
              type="button"
              className="um-btn-primary"
              onClick={handleAdd}
            >

              <span className="um-add-icon">

                <FiPlus size={16} />

              </span>

              Add Rent Duration

            </button>

          </div>

          {/* =================================================
              TABLE
          ================================================= */}

          <div className="table-responsive">

            <table className="table table-striped table-hover table-bordered align-middle mb-0">

              <thead className="table-light">

                <tr className="um-table-head">

                  <th className="um-th text-nowrap">
                    #
                  </th>

                  <th className="um-th text-nowrap text-center">
                    Season Name
                  </th>

                  <th className="um-th text-nowrap text-center">
                    From Date
                  </th>

                  <th className="um-th text-nowrap text-center">
                    To Date
                  </th>

                  <th className="um-th text-nowrap text-center">
                    Actions
                  </th>

                </tr>

              </thead>

              <tbody>

                {/* =================================================
                    LOADING
                ================================================= */}

                {loading ? (

                  <tr>

                    <td
                      colSpan={5}
                      className="um-td text-center"
                    >
                      Loading…
                    </td>

                  </tr>

                ) : data.length === 0 ? (

                  /* =================================================
                      EMPTY
                  ================================================= */

                  <tr>

                    <td
                      colSpan={5}
                      className="empty-table-cell text-center py-5"
                    >

                      <div className="empty-icon">
                        <FiClock/>
                      </div>

                      <div className="empty-title">
                        No records available
                      </div>

                      <div className="empty-sub">
                        Add a season duration to get started
                      </div>

                    </td>

                  </tr>

                ) : (

                  /* =================================================
                      DATA
                  ================================================= */

                  data.map(
                    (row, i) => {

                      const isEditing =
                        editId ===
                        row.season_durid;

                      return (
                        <tr
                          key={
                            row.season_durid
                          }
                          className={`um-row ${
                            isEditing
                              ? "editing-row"
                              : i % 2 === 0
                              ? "even-row"
                              : "odd-row"
                          }`}
                        >

                          {/* NUMBER */}

                          <td className="um-td user-number text-center">

                            {(
                              (page - 1) *
                                10 +
                              i +
                              1
                            )}

                          </td>

                          {/* SEASON */}

                          <td className="um-td text-center">

                            <span className="season-name-chip">

                              {
                                row.season_name ||
                                "—"
                              }

                            </span>

                          </td>

                          {/* FROM DATE */}

                          <td className="um-td text-center">

                            <span className="season-name-chip">

                              {fmtDate(
                                row.season_dur_fromdate
                              )}

                            </span>

                          </td>

                          {/* TO DATE */}

                          <td className="um-td text-center">

                            {fmtDate(
                              row.season_dur_todate
                            )}

                          </td>

                          {/* ACTIONS */}

                          <td className="um-td text-center season-action-td">

                            <div className="action-buttons d-flex justify-content-center align-items-center gap-2">

                              {/* EDIT */}

                              <button
                                type="button"
                                className="action-btn action-edit btn btn-sm"
                                title="Edit"
                                onClick={() =>
                                  handleEdit(
                                    row
                                  )
                                }
                              >

                                <FiEdit
                                  size={15}
                                />

                              </button>

                              {/* DELETE */}

                              <button
                                type="button"
                                className="action-btn action-delete btn btn-sm delete-enabled"
                                title="Delete"
                                onClick={() =>
                                  openDeleteModal(
                                    row
                                  )
                                }
                              >

                                <FiTrash2
                                  size={15}
                                />

                              </button>

                            </div>

                          </td>

                        </tr>
                      );
                    }
                  )

                )}

              </tbody>

            </table>

            {/* =================================================
                PAGINATION
            ================================================= */}

            {totalPages > 0 && (

              <div className="pagination">

                {/* INFO */}

                <span className="pagin-info">

                  {total > 0
                    ? `Showing ${
                        (page - 1) *
                          10 +
                        1
                      }–${Math.min(
                        page * 10,
                        total
                      )} of ${total}`
                    : ""}

                </span>

                {/* BUTTONS */}

                <div className="d-flex align-items-center gap-2">

                  {/* PREVIOUS */}

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={
                      page === 1 ||
                      loading
                    }
                    onClick={() =>
                      setPage((prev) =>
                        Math.max(
                          1,
                          prev - 1
                        )
                      )
                    }
                  >
                    ← Prev
                  </button>

                  {/* PAGE */}

                  <span className="pagin-info">

                    Page{" "}

                    {page}

                    {" / "}

                    {totalPages || 1}

                  </span>

                  {/* NEXT */}

                  <button
                    type="button"
                    className="pg-btn"
                    disabled={
                      page >=
                        totalPages ||
                      totalPages === 0 ||
                      loading
                    }
                    onClick={() =>
                      setPage((prev) =>
                        Math.min(
                          totalPages,
                          prev + 1
                        )
                      )
                    }
                  >
                    Next →
                  </button>

                </div>

              </div>

            )}

          </div>

        </div>

        {/* =====================================================
            ADD / EDIT MODAL
        ====================================================== */}

        {showModal && (

          <div
            className="um-overlay"
            onClick={handleCancel}
          >

            <div
              className="um-modal rent-duration-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              {/* =================================================
                  MODAL HEADER
              ================================================= */}

              <div className="um-modal-head">

                <div className="d-flex align-items-center gap-2">

                  <div className="form-card-icon">

                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}

                  </div>

                  <div>

                    <h2 className="um-modal-title mb-0">

                      {editId
                        ? "Edit Season Duration"
                        : "Add Season Duration"}

                    </h2>

                    <p className="form-card-sub mb-0">

                      {editId
                        ? "(Modify the selected season duration below)"
                        : "(Add a new season duration below)"}

                    </p>

                  </div>

                </div>

                {/* CLOSE */}

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  ✕
                </button>

              </div>

              {/* =================================================
                  MODAL BODY
              ================================================= */}

              <div className="um-modal-scroll">

                <div className="row g-3">

                  {/* =================================================
                      SEASON
                  ================================================= */}

                  <div className="field-box col-md-4">

                    <label className="field-label">

                      Season{" "}

                      <span className="text-danger">
                        *
                      </span>

                    </label>

                    <select
                      name="season_id"
                      value={
                        form.season_id
                      }
                      onChange={
                        handleChange
                      }
                      className="rd-input"
                    >

                      <option value="">
                        — Select Season —
                      </option>

                      {seasons.map(
                        (season) => (
                          <option
                            key={
                              season.season_id
                            }
                            value={
                              season.season_id
                            }
                          >
                            {
                              season.season_name
                            }
                          </option>
                        )
                      )}

                    </select>

                    <div className="field-hint">
                      Select the season
                    </div>

                  </div>

                  {/* =================================================
                      FROM DATE
                  ================================================= */}

                  <div className="field-box col-md-4">

                    <label className="field-label">

                      FROM — Date{" "}

                      <span className="text-danger">
                        *
                      </span>

                    </label>

                    <input
                      type="date"
                      name="season_dur_fromdate"
                      value={
                        form.season_dur_fromdate
                      }
                      onChange={
                        handleChange
                      }
                      className="rd-input"
                    />

                    <div className="field-hint">
                      Starting date of duration
                    </div>

                  </div>

                  {/* =================================================
                      TO DATE
                  ================================================= */}

                  <div className="field-box col-md-4">

                    <label className="field-label">

                      TO — Date{" "}

                      <span className="text-danger">
                        *
                      </span>

                    </label>

                    <input
                      type="date"
                      name="season_dur_todate"
                      value={
                        form.season_dur_todate
                      }
                      min={
                        form.season_dur_fromdate ||
                        undefined
                      }
                      onChange={
                        handleChange
                      }
                      className="rd-input"
                    />

                    <div className="field-hint">
                      Ending date of duration
                    </div>

                  </div>

                  {/* USER ID */}

                  <input
                    type="hidden"
                    value={
                      user?.user_id || ""
                    }
                    readOnly
                  />

                  {/* =================================================
                      BUTTONS
                  ================================================= */}

                  <div className="field-box submit-field col-md-12">

                    <div className="d-flex justify-content-end align-items-center gap-2">

                      {/* CANCEL */}

                      <button
                        type="button"
                        className="rd-btn-secondary clear-btn"
                        onClick={
                          handleCancel
                        }
                        disabled={
                          saving
                        }
                      >
                        Cancel
                      </button>

                      {/* SAVE / UPDATE */}

                      <button
                        type="button"
                        className="um-btn-primary"
                        onClick={
                          handleSubmit
                        }
                        disabled={
                          saving
                        }
                      >

                        {saving ? (

                          <span className="spinner" />

                        ) : editId ? (

                          <>

                            <FiEdit
                              size={15}
                            />

                            Update Duration

                          </>

                        ) : (

                          <>

                            <FiPlus
                              size={15}
                            />

                            Add Duration

                          </>

                        )}

                      </button>

                    </div>

                  </div>

                </div>

              </div>

            </div>

          </div>

        )}

      </main>

      {/* =====================================================
          DELETE MODAL
      ====================================================== */}

      {delModal && (

        <div
          className="um-overlay"
          onClick={
            closeDeleteModal
          }
        >

          <div
            className="um-modal um-delete-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="delete-content">

              {/* ICON */}

              <div className="delete-icon">

                <FiTrash2
                  size={32}
                />

              </div>

              {/* TITLE */}

              <h2 className="delete-title">

                Delete Season?

              </h2>

              {/* DESCRIPTION */}

              <p className="delete-description">

                Are you sure you want
                to delete{" "}

                <span className="badge badge-success">

                  "
                  {
                    delModal.season_name
                  }
                  "

                </span>

                ?

                <br />

                This will permanently
                remove this season
                duration.

                <br />

                <strong>
                  This action cannot
                  be undone.
                </strong>

              </p>

              {/* ACTIONS */}

              <div className="delete-actions">

                {/* CANCEL */}

                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={
                    closeDeleteModal
                  }
                  disabled={
                    delSaving
                  }
                >
                  Cancel
                </button>

                {/* DELETE */}

                <button
                  type="button"
                  disabled={
                    delSaving
                  }
                  className="delete-confirm-btn"
                  onClick={
                    handleDelete
                  }
                >

                  {delSaving ? (

                    <span className="spinner" />

                  ) : (

                    <>

                      <FiTrash2 />

                      Delete Permanently

                    </>

                  )}

                </button>

              </div>

            </div>

          </div>

        </div>

      )}

   

    </Layout>
  );
}
